import { addMonths } from 'date-fns';

import { formatDate } from 'lib/helpers/dateUtils';
import { inRn, mString, pluralize } from 'lib/helpers/utility';
import { WEEKS_PER_MONTH } from 'lib/constants/healthcareProgram/paymentSetup';

export const FREQUENCY_UNIT_LABELS = {
  weekly: 'Week',
  monthly: 'Month',
  yearly: 'Year'
};

const MONTHS_PER_YEAR = 12;

export const FREQUENCY_ADVERB_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly'
};

const getFrequencyUnit = (repeatEvent) =>
  (FREQUENCY_UNIT_LABELS[repeatEvent] ?? FREQUENCY_UNIT_LABELS.monthly).toLowerCase();

export const getCadenceText = (repeatEvent, repeatEvery) => {
  const every = Math.max(1, Number(repeatEvery) || 1);

  return `every ${every > 1 ? `${every} ` : ''}${pluralize(getFrequencyUnit(repeatEvent), every)}`;
};

export const getPaymentCadenceLabel = (repeatEvent, repeatEvery) => {
  const every = Math.max(1, Number(repeatEvery) || 1);

  if (every > 1) return `Payment ${getCadenceText(repeatEvent, every)}`;

  return `${FREQUENCY_ADVERB_LABELS[repeatEvent] ?? FREQUENCY_ADVERB_LABELS.monthly} payment`;
};

// config.min_amount floors each installment, which sets a CEILING on the installment count.
export const getMaxPayments = ({ remainingCents, minAmountPerPayment }) => {
  if (!minAmountPerPayment || minAmountPerPayment <= 0) return Infinity;
  if (!remainingCents || remainingCents <= 0) return Infinity;

  return Math.max(1, Math.floor(remainingCents / minAmountPerPayment));
};

// Unclamped installment count: a duration shorter than one interval yields 0, which is what
// makes an option unselectable (Letter's computeMaxPayments floors at 1 and would hide that).
export const countPayments = ({ repeatEvent, durationMonths, repeatEvery }) => {
  const every = Math.max(1, Number(repeatEvery) || 1);
  const months = Math.max(0, Number(durationMonths) || 0);

  if (repeatEvent === 'weekly') return Math.floor((months * WEEKS_PER_MONTH) / every);
  if (repeatEvent === 'yearly') return Math.floor(months / MONTHS_PER_YEAR / every);
  return Math.floor(months / every);
};

// The per-payment minimum sets a ceiling on the installment count. Returns the reason a plan
// breaches that ceiling, or null when the plan is offerable.
export const getPlanAmountCapError = ({ amount, recurringPayment, minAmountPerPayment }) => {
  if (!recurringPayment) return null;

  const remainingCents = Math.max(0, inRn(amount) - inRn(recurringPayment.upfrontPayment));
  const maxPayments = getMaxPayments({ remainingCents, minAmountPerPayment });

  if (inRn(recurringPayment.numberOfPayments) <= maxPayments) return null;

  return `The plan allows at most ${maxPayments} payments to keep each payment at or above the ${mString(minAmountPerPayment)} minimum. Please choose another payment method.`;
};

export const buildPlanFromDuration = ({
  recurringPayment,
  durationMonths,
  repeatEvent,
  repeatEvery,
  numberOfPayments,
  timezone
}) => {
  // The row drives the installment COUNT, so pin the mode that reads it — a plan left in
  // amount-per-payment mode would ignore numberOfPayments entirely.
  const next = {
    ...recurringPayment,
    paymentType: 'number-of-payments',
    amountPerPayment: null,
    repeatEvent,
    repeatEvery,
    numberOfPayments: Math.max(
      1,
      numberOfPayments ?? countPayments({ repeatEvent, durationMonths, repeatEvery })
    )
  };

  // Only carry the end date when the plan already tracks one, so the payload shape is unchanged.
  if (recurringPayment?.endsDate) {
    next.endsDate = formatDate(addMonths(new Date(), durationMonths), timezone, 'MM/dd/yyyy');
  }

  return next;
};
